import React, { useEffect, useState } from 'react';
import { RbacRoleWithPermissions, RbacPermission } from '../lib/types';
import { apiRequest } from '../lib/api';
import { ViewGuard } from '../components/auth/PermissionGuards';
import { SecurityLayout } from './security/layout';

interface RolesResponse {
  roles: RbacRoleWithPermissions[];
}

interface PermissionsResponse {
  permissions: RbacPermission[];
}

export default function RolesPage() {
  const [roles, setRoles] = useState<RbacRoleWithPermissions[]>([]);
  const [permissions, setPermissions] = useState<RbacPermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [rolesResp, permsResp] = await Promise.all([
        apiRequest('GET', '/api/roles'),
        apiRequest('GET', '/api/permissions')
      ]);

      if (rolesResp.ok) {
        const data: RolesResponse = await rolesResp.json();
        setRoles(data.roles || []);
      } else {
        setError('Failed to load roles');
      }

      if (permsResp.ok) {
        const data: PermissionsResponse = await permsResp.json();
        setPermissions(data.permissions || []);
      }
    } catch (err: any) {
      console.error('Error loading roles/permissions', err);
      setError('Failed to load roles/permissions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const renderPermissions = (role: RbacRoleWithPermissions) => {
    if (!role.permissions?.length) return 'None';
    return role.permissions.join(', ');
  };

  return (
    <ViewGuard section="userManagement">
      <SecurityLayout>
        <div className="space-y-4 px-4 sm:px-6 lg:px-8 pb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Roles</h1>
            <p className="text-sm text-gray-600">View roles and their permissions.</p>
          </div>

        {error && (
          <div className="bg-red-50 text-red-800 px-4 py-2 rounded border border-red-200">
            {error}
          </div>
        )}

        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Role List</h2>
              <p className="text-sm text-gray-600">Default roles seeded in RBAC.</p>
            </div>
            <button
              onClick={loadData}
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              disabled={isLoading}
            >
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {isLoading ? (
            <div className="p-6 text-gray-500">Loading roles...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {roles.map(role => (
                    <tr key={role.roleId}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{role.name}</div>
                        <div className="text-xs text-gray-500">{role.isSystem ? 'System role' : 'Custom role'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{role.description || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{renderPermissions(role)}</td>
                    </tr>
                  ))}
                  {roles.length === 0 && (
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-500" colSpan={3}>
                        No roles found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-md font-semibold text-gray-900">Permissions Directory</h3>
            <p className="text-sm text-gray-600">Available permission keys.</p>
          </div>
          <div className="p-4 grid md:grid-cols-2 gap-3">
            {permissions.map(p => (
              <div key={p.permissionId} className="border border-gray-200 rounded-lg p-3">
                <div className="font-medium text-gray-900">{p.key}</div>
                <div className="text-sm text-gray-600">{p.description || '—'}</div>
              </div>
            ))}
            {permissions.length === 0 && (
              <div className="text-gray-500 text-sm">No permissions found.</div>
            )}
          </div>
        </div>
        </div>
      </SecurityLayout>
    </ViewGuard>
  );
}
